// Sentry — error/replay/feedback SDK.
//
// `@sentry/react` (~9 KB gz + browser-tracing/replay/feedback integrations)
// is dynamically imported on `initSentry()` so the entry chunk stays slim.
// The user must grant analytics consent before any Sentry code is loaded.

let _initialized = false;
let _initInFlight: Promise<void> | null = null;

const CONSENT_STORAGE_KEY = "cookieConsent";
const CONSENT_REPROMPT_MS = 365 * 24 * 60 * 60 * 1000; // 365 days — match useCookieConsent

/**
 * Read the analytics-consent bit from the same localStorage record the consent
 * banner writes (`src/components/CookieBanner.tsx` / `useCookieConsent.ts`).
 * Returns false when no decision yet, when the record is stale (>365d), or when
 * the user explicitly rejected analytics. Tracking SDKs (Sentry, PostHog) must
 * not initialise until this returns true.
 */
export function readAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { analytics?: unknown; ts?: unknown };
    if (typeof parsed.analytics !== "boolean") return false;
    if (typeof parsed.ts !== "number") return false;
    if (Date.now() - parsed.ts > CONSENT_REPROMPT_MS) return false;
    return parsed.analytics === true;
  } catch {
    return false;
  }
}

/**
 * Initialise Sentry asynchronously. Bails early if consent is missing or the
 * DSN is unset. Idempotent — concurrent or repeat calls share the same
 * in-flight promise.
 */
export function initSentry(): Promise<void> {
  if (_initialized) return Promise.resolve();
  if (_initInFlight) return _initInFlight;
  // Consent gate (GDPR/CCPA): no analytics SDK init until the user opts in.
  // Includes the feedback widget — it ships error+breadcrumb context with each
  // report, which counts as analytics-tier data collection.
  if (!readAnalyticsConsent()) {
    if (import.meta.env.DEV) {
      console.info("[sentry] analytics consent not granted — Sentry disabled");
    }
    return Promise.resolve();
  }
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // Skip in dev / when DSN unset — avoids noisy local errors and accidental cross-env event delivery
    if (import.meta.env.DEV) {
      console.info("[sentry] VITE_SENTRY_DSN not set — Sentry disabled");
    }
    return Promise.resolve();
  }
  _initInFlight = (async () => {
    try {
      const [Sentry, { sentryBeforeSend }] = await Promise.all([
        import("@sentry/react"),
        import("./sentryBeforeSend"),
      ]);
      Sentry.init({
        dsn,
        environment: import.meta.env.MODE,
        release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
        // Browser tracing: 5% sampling in prod, off in dev/test.
        tracesSampleRate: import.meta.env.PROD ? 0.05 : 0,
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 1.0,
        // Drop noisy chunk-load / network / abort events client-side; see
        // src/lib/sentryBeforeSend.ts for the rules.
        beforeSend: sentryBeforeSend,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
          Sentry.feedbackIntegration({
            colorScheme: "system",
            showBranding: false,
            buttonLabel: "Report a bug",
            formTitle: "Report a bug or send feedback",
            submitButtonLabel: "Send",
            messagePlaceholder: "What went wrong? Steps to reproduce help us fix it faster.",
            successMessageText: "Thanks — we got it.",
          }),
        ],
      });
      _initialized = true;
    } catch (e) {
      console.error("[sentry] init failed:", e);
    } finally {
      _initInFlight = null;
    }
  })();
  return _initInFlight;
}

export function isSentryEnabled(): boolean {
  return _initialized;
}

/** Test-only — reset module state between specs. */
export function __resetSentryForTests(): void {
  _initialized = false;
  _initInFlight = null;
}
